import React from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ScrollText,
  ArrowUpFromLine,
  Trash2,
  RefreshCw,
  QrCode,
  History,
  Settings2,
  Repeat,
  Shield,
  Copy,
  Clock,
  Check,
  LogIn,
  LogOut,
  Info,
  Eye,
  EyeOff,
  X,
  ChevronDown,
} from 'lucide-react';
import type { IAccountDataResponse } from '@tool-no-hu/shared/types';
import {
  getAccountCredentialsService,
  type AccountCredentials,
} from '../service/account';

// ─── Types ────────────────────────────────────────────────────────────────

interface AccountCardProps {
  acc: IAccountDataResponse;
  onDelete: (id: string) => void;
  onGetBalance?: (id: string) => void;
  onGetUSDTQR?: (id: string) => void;
  onGetHistory?: (id: string) => void;
  onGetBetLogs?: (id: string) => void;
  onWithdraw?: (id: string) => void;
  onLogin?: (id: string) => Promise<void>;
  onLogout?: (id: string) => Promise<void>;
  isInGroup?: boolean;
  deleteDisabled?: boolean;
  headerColor?: string;
}

// ─── Helpers ───────────────────────────────────────────────────────────────

function fmt(val: number | '?' | undefined): string {
  if (val === '?' || val == null) return '?';
  return val.toLocaleString('vi-VN');
}

/** IP = phần host của chuỗi proxy "ip:port[:user:pass]". */
function ipFromProxy(proxy?: string): string {
  if (!proxy || proxy === 'No Proxy') return '';
  return proxy.split(':')[0] || '';
}

// ─── Action Button ─────────────────────────────────────────────────────────

const ActionBtn: React.FC<{
  children: React.ReactNode;
  onClick: () => void;
  disabled?: boolean;
  color?: 'blue' | 'red' | 'orange' | 'cyan' | 'purple' | 'teal' | 'default';
  full?: boolean;
}> = ({ children, onClick, disabled, color = 'default', full = false }) => {
  const colors: Record<string, string> = {
    blue: 'bg-[#3b82f6]/10 text-[#3b82f6] hover:bg-[#3b82f6]/20',
    red: 'bg-[#ef4444]/10 text-[#ef4444] hover:bg-[#ef4444]/20',
    orange: 'bg-[#f59e0b]/10 text-[#f59e0b] hover:bg-[#f59e0b]/20',
    cyan: 'bg-[#06b6d4]/10 text-[#06b6d4] hover:bg-[#06b6d4]/20',
    purple: 'bg-[#a855f7]/10 text-[#a855f7] hover:bg-[#a855f7]/20',
    teal: 'bg-[#14b8a6]/10 text-[#14b8a6] hover:bg-[#14b8a6]/20',
    default: 'bg-[#252a35] text-[#e2e8f0] hover:bg-[#2f3545]',
  };
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`flex items-center justify-center gap-1.5 rounded-lg px-3 py-1.5 text-[10px] font-bold transition-all duration-150 disabled:cursor-not-allowed disabled:opacity-50 ${full ? 'w-full h-full' : ''} ${colors[color]}`}
    >
      {children}
    </button>
  );
};

// ─── AccountCard ───────────────────────────────────────────────────────────

export const AccountCard: React.FC<AccountCardProps> = ({
  acc,
  onDelete,
  onGetBalance,
  onGetUSDTQR,
  onGetHistory,
  onGetBetLogs,
  onWithdraw,
  onLogin,
  onLogout,
  isInGroup = false,
  deleteDisabled = false,
  headerColor,
}) => {
  const isLoggedIn = !!acc.accessToken;
  const [detailsOpen, setDetailsOpen] = React.useState(false);
  const [confirmDelete, setConfirmDelete] = React.useState(false);
  const [copied, setCopied] = React.useState(false);
  const [authAction, setAuthAction] = React.useState<'login' | 'logout' | null>(null);

  // ── Modal "Thông tin tài khoản" ────────────────────────────────────────
  // Password không nằm trong payload danh sách (sảnh poll liên tục) nên chỉ
  // fetch khi user thực sự mở modal.
  const [infoOpen, setInfoOpen] = React.useState(false);
  const [infoLoading, setInfoLoading] = React.useState(false);
  const [info, setInfo] = React.useState<AccountCredentials | null>(null);
  const [infoError, setInfoError] = React.useState(false);
  const [showPassword, setShowPassword] = React.useState(false);
  const [copiedField, setCopiedField] = React.useState<string | null>(null);

  const openInfo = async () => {
    setInfoOpen(true);
    setShowPassword(false);
    setCopiedField(null);
    if (info) return;
    setInfoLoading(true);
    setInfoError(false);
    const data = await getAccountCredentialsService(acc._id || '');
    if (data) setInfo(data);
    else setInfoError(true);
    setInfoLoading(false);
  };

  const copyField = (field: string, value: string) => {
    if (!value) return;
    navigator.clipboard.writeText(value).catch(() => {
      const ta = document.createElement('textarea');
      ta.value = value;
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
    });
    setCopiedField(field);
    setTimeout(() => setCopiedField(null), 1500);
  };

  React.useEffect(() => {
    if (!infoOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setInfoOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [infoOpen]);

  const handleLogin = async () => {
    if (!onLogin) return;
    setAuthAction('login');
    try {
      await onLogin(acc._id || '');
    } finally {
      setAuthAction(null);
    }
  };

  const handleLogout = async () => {
    if (!onLogout) return;
    setAuthAction('logout');
    try {
      await onLogout(acc._id || '');
    } finally {
      setAuthAction(null);
    }
  };

  const isRunning = acc.status === 'RUNNING';
  const balanceStr = fmt(acc.balance);
  const digitLen = balanceStr.replace(/[^0-9]/g, '').length;
  const metricSize =
    digitLen <= 6 ? '24px' :
    digitLen <= 9 ? '17px' :
    '14px';
  const tokenText = acc.tokenTime || 'No Token';
  const tokenSize =
    tokenText.length <= 7 ? '20px' :
    tokenText.length <= 10 ? '17px' :
    '14px';
  const clockSize = tokenText.length <= 10 ? 'h-3.5 w-3.5' : 'h-3 w-3';
  const winRate = acc.rounds.total > 0 ? (acc.rounds.w / acc.rounds.total) * 100 : 0;
  const barColor = winRate >= 50 ? '#22c55e' : winRate >= 30 ? '#f59e0b' : '#ef4444';

  const handleDeleteClick = () => {
    if (confirmDelete) {
      onDelete(acc._id || '');
      setConfirmDelete(false);
    } else {
      setConfirmDelete(true);
    }
  };

  const handleCopyProxy = () => {
    if (!acc.proxy) return;
    navigator.clipboard.writeText(acc.proxy).catch(() => {
      const ta = document.createElement('textarea');
      ta.value = acc.proxy!;
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
    });
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className="flex flex-col rounded-xl border border-[#252a38] bg-[#1c2030] text-[#e2e8f0] shadow-sm"
    >
      {/* ── Header ───────────────────────────────────────────────────── */}
      <div className="flex items-center justify-between gap-2 px-4 pt-4 pb-3">
        <div className="min-w-0 flex-1">
          <div className="text-sm font-bold truncate" title={acc.username} style={{ color: headerColor || '#e2e8f0' }}>{acc.username}</div>
          <div className="mt-0.5 text-[11px] truncate" title={acc.displayname} style={{ color: headerColor ? `${headerColor}99` : '#94a3b8' }}>{acc.displayname}</div>
        </div>

        {/* Status badge — cấu hình cược đã gộp vào nút sửa nhóm ở GroupCard */}
        <div className="flex shrink-0 items-center gap-1">
          <div
            className={`whitespace-nowrap rounded-full px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider ${
              isRunning
                ? 'bg-[#22c55e]/10 text-[#22c55e]'
                : 'bg-[#64748b]/10 text-[#64748b]'
            }`}
          >
            {isRunning && (
              <motion.span
                animate={{ opacity: [1, 0.3, 1] }}
                transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
                className="inline-block h-1.5 w-1.5 rounded-full bg-[#22c55e] mr-1.5 align-middle"
              />
            )}
            {isRunning ? 'Đang chạy' : 'Đã dừng'}
          </div>
        </div>
      </div>

      {/* ── Primary metrics ─────────────────────────────────────────── */}
      <div className="grid grid-cols-[1fr_1fr] px-4 pb-4 overflow-hidden">
        <div className="flex flex-col items-start gap-1 min-w-0 overflow-hidden">
          <span className="text-[11px] font-medium uppercase tracking-[0.08em] text-[#64748b]">
            Số dư
          </span>
          <span className="w-full whitespace-nowrap overflow-hidden text-ellipsis font-mono font-bold leading-none tracking-tight text-[#e2e8f0]"
            style={{ fontSize: metricSize }}>
            {balanceStr}
          </span>
        </div>
        <div className="flex flex-col items-start gap-1 border-l border-[#252a38] pl-4 min-w-0 overflow-hidden">
          <span className="text-[11px] font-medium uppercase tracking-[0.08em] text-[#64748b]">
            Token
          </span>
          <span
            className={`flex w-full items-center gap-1 whitespace-nowrap overflow-hidden text-ellipsis font-mono font-bold leading-none tracking-tight ${
              acc.tokenWarning ? 'text-[#f59e0b]' : 'text-[#e2e8f0]'
            }`}
            style={{ fontSize: tokenSize }}
            title={tokenText}
          >
            {acc.tokenWarning && <Clock className={`shrink-0 text-[#f59e0b] ${clockSize}`} />}
            <span className="min-w-0 overflow-hidden text-ellipsis">{tokenText}</span>
          </span>
        </div>
      </div>

      <div className="border-t border-[#252a38]" />

      {/* ── Details toggle — config/rounds/proxy ẩn mặc định, bấm mới xổ ra ── */}
      <button
        type="button"
        onClick={() => setDetailsOpen((v) => !v)}
        className="flex w-full items-center justify-between gap-2 bg-[#08111c] px-4 py-2 text-left transition-colors hover:bg-[#0d1622]"
      >
        <span className="flex min-w-0 items-center gap-2 overflow-hidden">
          <Repeat className="h-3 w-3 shrink-0 text-[#334155]" />
          <span className="shrink-0 font-mono text-[11px] whitespace-nowrap"
            style={{ color: acc.rounds.total > 0 ? barColor : '#64748b' }}>
            {acc.rounds.total}({acc.rounds.w}W/{acc.rounds.l}L)
          </span>
          <span className="min-w-0 overflow-hidden text-ellipsis whitespace-nowrap font-mono text-[11px] text-[#4a5568]">
            {acc.proxy?.split(':')[0] || 'N/A'}
          </span>
        </span>
        <span className="flex shrink-0 items-center gap-1 text-[10px] font-semibold text-[#64748b]">
          Chi tiết
          <ChevronDown
            className={`h-3.5 w-3.5 transition-transform ${detailsOpen ? 'rotate-180' : ''}`}
          />
        </span>
      </button>

      <AnimatePresence initial={false}>
        {detailsOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.15 }}
            className="overflow-hidden"
          >
            <div className="space-y-0 bg-[#08111c] px-4 pb-3">
              {/* Config — wraps, no truncate */}
              <div className="flex items-start gap-2 py-2 border-b border-[#11182a]">
                <Settings2 className="h-[13px] w-[13px] shrink-0 text-[#334155] mt-[7px]" />
                <span className="flex-1 min-w-0 rounded-md border border-[#1e2937] bg-[#0e131c] px-2.5 py-1 font-mono text-[12px] leading-[1.6] text-[#7dd3fc]"
                  style={{ whiteSpace: 'normal', wordBreak: 'break-all' }}>
                  {acc.config}
                </span>
              </div>

              {/* Rounds — inline with win rate % + progress bar */}
              <div className="flex items-center gap-2 py-2 border-b border-[#11182a]">
                <Repeat className="h-[13px] w-[13px] shrink-0 text-[#334155]" />
                {acc.rounds.total > 0 && (
                  <div className="h-[3px] w-20 shrink-0 overflow-hidden rounded-[2px] bg-[#0e1424]">
                    <div className="h-full rounded-[2px] transition-all duration-300"
                      style={{ width: `${winRate}%`, backgroundColor: barColor }}
                    />
                  </div>
                )}
                <span className="font-mono text-[12px] whitespace-nowrap"
                  style={{ color: acc.rounds.total > 0 ? barColor : '#94a3b8' }}>
                  {acc.rounds.total}({acc.rounds.w}W/{acc.rounds.l}L) {acc.rounds.total > 0 ? `${Math.round(winRate)}%` : acc.rounds.rate}
                </span>
              </div>

              {/* Proxy — inline, last row, no trailing border */}
              <div className="flex items-center gap-2 py-2">
                <Shield className="h-[13px] w-[13px] shrink-0 text-[#334155]" />
                <span className="h-2 w-2 shrink-0 rounded-full bg-[#22c55e]" />
                <span className="min-w-0 overflow-hidden text-ellipsis whitespace-nowrap font-mono text-xs text-[#4a5568]" title={acc.proxy || ''}>
                  {acc.proxy?.split(':')[0] || 'N/A'}
                </span>
                <button
                  onClick={handleCopyProxy}
                  className="shrink-0 p-0.5 text-[#334155] transition-colors hover:text-[#7dd3fc]"
                  title={copied ? 'Đã sao chép' : 'Sao chép proxy'}
                >
                  {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="border-t border-[#252a38]" />

      {/* ── Actions ──────────────────────────────────────────────────── */}
      {isInGroup ? (
        <div className="grid grid-cols-3 gap-1.5 p-3">
          {/* Thông tin tài khoản — full width, mở modal xem username/pass/proxy/IP/số dư */}
          <div className="col-span-3">
            <ActionBtn onClick={openInfo} color="default" full>
              <Info className="h-3.5 w-3.5" />
              Thông tin tài khoản
            </ActionBtn>
          </div>

          {/* Hàng trên: QR Nạp - Rút - Balance. Không ghi tên coin/network vào nhãn:
              loại tiền và mạng lưới do paygate quyết định, user chọn trong modal. */}
          <ActionBtn
            onClick={() => onGetUSDTQR?.(acc._id || '')}
            disabled={!isLoggedIn}
            color="purple"
            full
          >
            <QrCode className="h-3.5 w-3.5" />
            QR Nạp
          </ActionBtn>
          <ActionBtn
            onClick={() => onWithdraw?.(acc._id || '')}
            disabled={!isLoggedIn}
            color="orange"
            full
          >
            <ArrowUpFromLine className="h-3.5 w-3.5" />
            Rút
          </ActionBtn>
          <ActionBtn
            onClick={() => onGetBalance?.(acc._id || '')}
            disabled={!isLoggedIn}
            color="cyan"
            full
          >
            <RefreshCw className="h-3.5 w-3.5" />
            Balance
          </ActionBtn>

          {/* Hàng dưới: Bet Log - Ls Nạp/Rút - Delete */}
          <ActionBtn
            onClick={() => onGetBetLogs?.(acc._id || '')}
            disabled={!isLoggedIn}
            color="blue"
            full
          >
            <ScrollText className="h-3.5 w-3.5" />
            Bet Log
          </ActionBtn>
          <ActionBtn
            onClick={() => onGetHistory?.(acc._id || '')}
            disabled={!isLoggedIn}
            color="teal"
            full
          >
            <History className="h-3.5 w-3.5" />
            Ls Nạp/Rút
          </ActionBtn>

          {/* Delete — inline confirm */}
          <AnimatePresence mode="wait">
            {confirmDelete ? (
              <motion.div
                key="confirm"
                initial={{ opacity: 0, x: -8 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 8 }}
                transition={{ duration: 0.12 }}
                className="flex h-full w-full items-center justify-center gap-1.5 rounded-lg bg-[#ef4444]/10 px-2 py-1.5"
              >
                <span className="text-[9px] text-[#ef4444] font-bold">Xóa?</span>
                <button
                  onClick={handleDeleteClick}
                  className="rounded px-1.5 py-0.5 text-[9px] font-bold text-[#ef4444] transition-colors hover:bg-[#ef4444]/20"
                >
                  Xóa
                </button>
                <button
                  onClick={() => setConfirmDelete(false)}
                  className="rounded px-1.5 py-0.5 text-[9px] font-bold text-[#64748b] transition-colors hover:bg-[#252a35]"
                >
                  Hủy
                </button>
              </motion.div>
            ) : (
              <motion.div
                key="del-btn"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="h-full w-full"
              >
                <ActionBtn
                  onClick={handleDeleteClick}
                  disabled={deleteDisabled}
                  color="red"
                  full
                >
                  <Trash2 className="h-3.5 w-3.5" />
                  Delete
                </ActionBtn>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      ) : (
        /* Ungrouped — login/logout + full actions */
        <div className="space-y-1.5 p-3">
          {/* Login / Logout */}
          <div className="grid grid-cols-2 gap-1.5">
            <ActionBtn
              onClick={handleLogin}
              disabled={isLoggedIn || authAction !== null}
              color="blue"
              full
            >
              {authAction === 'login' ? (
                <span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-[#3b82f6] border-t-transparent" />
              ) : (
                <LogIn className="h-3.5 w-3.5" />
              )}
              Login
            </ActionBtn>
            <ActionBtn
              onClick={handleLogout}
              disabled={!isLoggedIn || isRunning || authAction !== null}
              color="default"
              full
            >
              {authAction === 'logout' ? (
                <span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-[#64748b] border-t-transparent" />
              ) : (
                <LogOut className="h-3.5 w-3.5" />
              )}
              Logout
            </ActionBtn>
          </div>

          {/* 6 actions */}
          <div className="grid grid-cols-3 gap-1.5">
            {/* Thông tin tài khoản — full width, mở modal xem username/pass/proxy/IP/số dư */}
            <div className="col-span-3">
              <ActionBtn onClick={openInfo} color="default" full>
                <Info className="h-3.5 w-3.5" />
                Thông tin tài khoản
              </ActionBtn>
            </div>

            <ActionBtn onClick={() => onGetUSDTQR?.(acc._id || '')} disabled={!isLoggedIn} color="purple" full>
              <QrCode className="h-3.5 w-3.5" />
              QR Nạp
            </ActionBtn>
            <ActionBtn onClick={() => onWithdraw?.(acc._id || '')} disabled={!isLoggedIn} color="orange" full>
              <ArrowUpFromLine className="h-3.5 w-3.5" />
              Rút
            </ActionBtn>
            <ActionBtn onClick={() => onGetBalance?.(acc._id || '')} disabled={!isLoggedIn} color="cyan" full>
              <RefreshCw className="h-3.5 w-3.5" />
              Balance
            </ActionBtn>

            <ActionBtn onClick={() => onGetBetLogs?.(acc._id || '')} disabled={!isLoggedIn} color="blue" full>
              <ScrollText className="h-3.5 w-3.5" />
              Bet Log
            </ActionBtn>
            <ActionBtn onClick={() => onGetHistory?.(acc._id || '')} disabled={!isLoggedIn} color="teal" full>
              <History className="h-3.5 w-3.5" />
              Ls Nạp/Rút
            </ActionBtn>

            {/* Delete — inline confirm */}
            <AnimatePresence mode="wait">
              {confirmDelete ? (
                <motion.div
                  key="confirm"
                  initial={{ opacity: 0, x: -8 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 8 }}
                  transition={{ duration: 0.12 }}
                  className="flex h-full w-full items-center justify-center gap-1.5 rounded-lg bg-[#ef4444]/10 px-2 py-1.5"
                >
                  <span className="text-[9px] text-[#ef4444] font-bold">Xóa?</span>
                  <button
                    onClick={handleDeleteClick}
                    className="rounded px-1.5 py-0.5 text-[9px] font-bold text-[#ef4444] transition-colors hover:bg-[#ef4444]/20"
                  >
                    Xóa
                  </button>
                  <button
                    onClick={() => setConfirmDelete(false)}
                    className="rounded px-1.5 py-0.5 text-[9px] font-bold text-[#64748b] transition-colors hover:bg-[#252a35]"
                  >
                    Hủy
                  </button>
                </motion.div>
              ) : (
                <motion.div
                  key="del-btn"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="h-full w-full"
                >
                  <ActionBtn
                    onClick={handleDeleteClick}
                    disabled={deleteDisabled}
                    color="red"
                    full
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                    Delete
                  </ActionBtn>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      )}

      {/* ── Modal thông tin tài khoản ─────────────────────────────────── */}
      {/* Portal ra body: card nằm trong motion.div có transform, `fixed` bên
          trong sẽ neo theo card thay vì viewport. */}
      {infoOpen &&
        createPortal(
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-[#0d0f14]/80 p-4 backdrop-blur-sm"
            onClick={() => setInfoOpen(false)}
          >
            <div
              className="relative flex w-full max-w-md flex-col rounded-2xl border border-[#252a38] bg-[#161920] p-6 shadow-xl"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                onClick={() => setInfoOpen(false)}
                className="absolute right-4 top-4 rounded-full p-1 text-[#64748b] transition-colors hover:bg-[#252a35] hover:text-[#e2e8f0]"
              >
                <X className="h-5 w-5" strokeWidth={2.5} />
              </button>

              <h3 className="mb-1 text-lg font-bold text-[#e2e8f0]">Thông tin tài khoản</h3>
              <p className="mb-5 text-xs font-medium text-[#64748b]">
                Tài khoản: <span className="font-semibold text-[#e2e8f0]">{acc.username}</span>
              </p>

              {infoLoading ? (
                <div className="flex items-center justify-center gap-2 py-8 text-xs font-medium text-[#64748b]">
                  <span className="h-4 w-4 animate-spin rounded-full border-2 border-[#3b82f6] border-t-transparent" />
                  Đang tải thông tin...
                </div>
              ) : infoError || !info ? (
                <div className="py-8 text-center text-xs font-medium text-[#ef4444]">
                  Không lấy được thông tin tài khoản.
                </div>
              ) : (
                <div className="space-y-2">
                  <InfoRow
                    label="Username"
                    value={info.username}
                    copied={copiedField === 'username'}
                    onCopy={() => copyField('username', info.username)}
                  />
                  <InfoRow
                    label="Tên hiển thị"
                    value={info.displayname}
                    copied={copiedField === 'displayname'}
                    onCopy={() => copyField('displayname', info.displayname)}
                  />
                  <InfoRow
                    label="Mật khẩu"
                    value={showPassword ? info.password : '•'.repeat(info.password?.length || 0)}
                    copied={copiedField === 'password'}
                    onCopy={() => copyField('password', info.password)}
                    onToggle={() => setShowPassword((v) => !v)}
                    toggled={showPassword}
                  />
                  <InfoRow
                    label="Proxy"
                    value={info.proxy}
                    copied={copiedField === 'proxy'}
                    onCopy={() => copyField('proxy', info.proxy)}
                  />
                  <InfoRow
                    label="Customer ID"
                    value={acc.customerId || ''}
                    copied={copiedField === 'customerId'}
                    onCopy={() => copyField('customerId', acc.customerId || '')}
                  />
                  <InfoRow
                    label="IP"
                    value={ipFromProxy(info.proxy)}
                    copied={copiedField === 'ip'}
                    onCopy={() => copyField('ip', ipFromProxy(info.proxy))}
                  />
                  <InfoRow
                    label="Số dư"
                    value={balanceStr}
                    copied={copiedField === 'balance'}
                    onCopy={() => copyField('balance', balanceStr)}
                  />
                </div>
              )}
            </div>
          </div>,
          document.body,
        )}
    </motion.div>
  );
};

// ─── InfoRow — 1 dòng trong modal thông tin tài khoản ──────────────────────

const InfoRow: React.FC<{
  label: string;
  value: string;
  copied: boolean;
  onCopy: () => void;
  onToggle?: () => void;
  toggled?: boolean;
}> = ({ label, value, copied, onCopy, onToggle, toggled }) => (
  <div className="flex items-center gap-3 rounded-lg border border-[#252a38] bg-[#0e131c] px-3 py-2.5">
    <span className="w-24 shrink-0 text-[10px] font-bold uppercase tracking-wider text-[#64748b]">
      {label}
    </span>
    <span
      className="min-w-0 flex-1 break-all font-mono text-[12px] text-[#e2e8f0]"
      title={value}
    >
      {value || '-'}
    </span>
    {onToggle && (
      <button
        type="button"
        onClick={onToggle}
        className="shrink-0 rounded p-1 text-[#64748b] transition-colors hover:bg-[#1e2235] hover:text-[#e2e8f0]"
        title={toggled ? 'Ẩn mật khẩu' : 'Hiện mật khẩu'}
      >
        {toggled ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
      </button>
    )}
    <button
      type="button"
      onClick={onCopy}
      className="shrink-0 rounded p-1 text-[#64748b] transition-colors hover:bg-[#1e2235] hover:text-[#7dd3fc]"
      title={copied ? 'Đã sao chép' : 'Sao chép'}
    >
      {copied ? (
        <Check className="h-3.5 w-3.5 text-[#22c55e]" />
      ) : (
        <Copy className="h-3.5 w-3.5" />
      )}
    </button>
  </div>
);
